// frontend-crm/src/pages/SuperAdmin/SuperAdminStats.js
import React from 'react';
import {
  Box, Grid, Typography, Card, CardContent,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import { useQuery } from 'react-query';

const API_URL = process.env.REACT_APP_API_URL || '';
const COLORS = ['#6C63FF', '#00D9A5', '#FF6B6B', '#FFB347', '#8B5CF6'];

const fetchStats = () =>
  fetch(`${API_URL}/api/superadmin/tenants/stats/global`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json());

const fetchTenants = () =>
  fetch(`${API_URL}/api/superadmin/tenants`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json());

export default function SuperAdminStats() {
  const { data: statsRes, isLoading: statsLoading } = useQuery('superadmin-stats-detailed', fetchStats);
  const { data: tenantsRes, isLoading: tenantsLoading } = useQuery('superadmin-tenants-stats', fetchTenants);

  const stats = statsRes?.data;
  const tenants = tenantsRes?.data || [];

  if (statsLoading || tenantsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#6C63FF' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mb: 4 }}>
        Estadísticas Avanzadas
      </Typography>

      <Grid container spacing={3}>
        {/* Gráfico de Ingresos Globales */}
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#fff', mb: 3 }}>
                Ingresos Diarios (Global)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats?.grafico_datos || []}>
                  <defs>
                    <linearGradient id="ingresosGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00D9A5" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00D9A5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="dia" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ bgcolor: '#1A1F2E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Area type="monotone" dataKey="ingresos" stroke="#00D9A5" strokeWidth={2} fill="url(#ingresosGrad)" name="Ingresos ($)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Órdenes por Tipo de Servicio */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#fff', mb: 3 }}>
                Distribución por Servicio
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats?.por_servicio || []}
                    cx="50%" cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="total"
                    nameKey="tipo_servicio"
                  >
                    {(stats?.por_servicio || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Tenants por Pedidos */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#fff', mb: 3 }}>
                Rendimiento por Empresa (Ranking)
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.4)' }}>Empresa</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.4)' }}>Pedidos Hoy</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.4)' }}>Usuarios Totales</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.4)' }}>Estado Bot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map(t => (
                    <TableRow key={t.id}>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t.nombre}</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{t.pedidos_hoy}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.6)' }}>{t.usuarios_count}</TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            color: t.bot_status === 'connected' ? '#00D9A5' : '#FF6B6B',
                            fontWeight: 700
                          }}
                        >
                          {t.bot_status.toUpperCase()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
